import { normalizeStorageError, startUpload, UploadFileInput, UploadProgress, UploadTask } from '@Utils/S3Storage'
import S3Remover from '@Utils/S3Remover'
import { v4 as uuidv4 } from 'uuid'
import toastr from 'toastr'

import FileNameSanitizer from '@Utils/FileNameSanitizer'

import 'toastr/build/toastr.css'

const AUDIO_EXTENSION = 'mp3'

const TOASTR_OPTIONS = {
  closeButton: true,
  timeOut: 0,
  extendedTimeOut: 0
}

export interface IElementUploaderOptions {
  readonly workspaceId: number
  readonly mediaDimensions?: IElementMediaDimensions
  uploadElementCallback: (params: IUploadedElement) => void
  showProgressCallback: (data: UploadProgress) => void
}

export interface IElementMediaDimensions {
  readonly width: number
  readonly height: number
}

export interface IUploadedElement {
  readonly width: number
  readonly height: number
  readonly url: string
  readonly filename: string
  readonly kind: string
}

interface IElementMetaData {
  readonly width: number
  readonly height: number
  readonly size: number
  readonly filename: string
  readonly loaded: boolean
}

class ElementUploader {
  workspaceId: number
  filename: string
  sanitizedFileName: string
  element: HTMLVideoElement
  elementMetaData: IElementMetaData
  private readonly elementMetaDataPromise: Promise<IElementMetaData | undefined>
  uploadElementCallback: (params: IUploadedElement) => void
  showProgressCallback: (data: UploadProgress) => void

  constructor(
    private readonly file: File,
    options: IElementUploaderOptions,
  ) {
    this.file = file
    this.elementMetaData = { width: 0, height: 0, size: 0, filename: '', loaded: false }
    this.filename = decodeURI(encodeURI(this.file.name))
    // we have to take only the first 50 chars to suppress the error for tempfiles:
    // Filename too long @rb_sysopen - /tmp/...
    // the maximum tempfile's name is 170 chars, but we also add a UUID to it.
    this.sanitizedFileName = new FileNameSanitizer(this.filename).call().substring(0,50)
    this.uploadElementCallback = options.uploadElementCallback
    this.showProgressCallback = options.showProgressCallback
    this.element = document.createElement('video')
    this.elementMetaDataPromise = options.mediaDimensions
      ? Promise.resolve({
        ...options.mediaDimensions,
        size: this.file.size,
        filename: this.filename,
        loaded: true,
      })
      : this._initializeElementMetaData()
    this.workspaceId = options.workspaceId
  }

  call(): UploadTask {
    const params = this._generateAwsParams()
    return this._upload(params)
  }

  _upload(params: UploadFileInput): UploadTask {
    const { completion, task } = startUpload(params, this.showProgressCallback)

    void completion
      .then(async url => {
        const fileExtension = this.file.name.split('.').pop()
        const kind = fileExtension === AUDIO_EXTENSION ? 'audio' : 'video'
        const elementMetaData = await this.elementMetaDataPromise

        if (elementMetaData) {
          this.uploadElementCallback({
            ...elementMetaData,
            url,
            kind
          })
        } else {
          toastr.error('There is a problem with the format of your file. Your file could be corrupted. Please check it.', 'Upload cannot be completed!', TOASTR_OPTIONS)
          const remover = new S3Remover({ fileUrl: url })
          void remover.call().catch(() => undefined)
        }
      })
      .catch((error: unknown) => {
        const uploadError = normalizeStorageError(error)

        if (uploadError.name === 'AbortError') {
          return
        } else if (uploadError.name === 'RequestTimeTooSkewed') {
          toastr.error('Please adjust the system time of your device. Then try the uploading again.', 'Element cannot be uploaded!', TOASTR_OPTIONS)
        } else {
          toastr.error('Please try again. If the problem persists, contact support.', 'Upload cannot be completed!', TOASTR_OPTIONS)
        }
        if (!window.Rollbar) { return }
        window.Rollbar.error(uploadError)
      })

    return task
  }

  _generateAwsParams(): UploadFileInput {
    const fileExtension = this.filename.split('.').pop()
    const workspaceIdForUpload = this.workspaceId >= 0 ? this.workspaceId : 'shared'
    const bucketName = window.EditorSources ? `${window.EditorSources.s3Bucket}/workspace/${workspaceIdForUpload}` : `${window.Checksub.s3Bucket}/workspace/${workspaceIdForUpload}`

    // Characters That Might Require Special Handling
    // https://docs.aws.amazon.com/en_us/AmazonS3/latest/dev/UsingMetadata.html
    const key = encodeURI(`${this.sanitizedFileName}_${uuidv4()}.${fileExtension}`)

    return {
      body: this.file,
      bucket: bucketName,
      contentType: this.file.type,
      key,
    }
  }

  _initializeElementMetaData(): Promise<IElementMetaData | undefined> {
    const element = this.element
    element.preload = 'metadata'
    const objectUrl = URL.createObjectURL(this.file)

    return new Promise(resolve => {
      const cleanup = () => {
        element.removeEventListener('loadedmetadata', handleLoadedMetaData)
        element.removeEventListener('error', handleError)
        element.removeAttribute('src')
        element.load()
        URL.revokeObjectURL(objectUrl)
      }

      const handleLoadedMetaData = () => {
        this.elementMetaData = {
          height: element.videoHeight,
          width: element.videoWidth,
          size: this.file.size,
          filename: this.filename,
          loaded: true
        }
        cleanup()
        resolve(this.elementMetaData)
      }

      const handleError = () => {
        cleanup()
        resolve(undefined)
      }

      element.addEventListener('loadedmetadata', handleLoadedMetaData)
      element.addEventListener('error', handleError)
      element.src = objectUrl
    })
  }
}

export default ElementUploader
